import React, { useMemo } from 'react';
import config from '../../../config';
import { DiscussionEntityRef } from '../domain/types';
import { createDiscussionService } from '../application/DiscussionService';
import { createApiDiscussionRepository } from '../infrastructure/apiRepository';
import { createLocalStorageDiscussionRepository } from '../infrastructure/localStorageRepository';
import { createSignalRDiscussionTransport } from '../infrastructure/signalRTransport';
import { useDiscussionController } from './useDiscussionController';
import DiscussionCore from './DiscussionCore';

interface DiscussionPanelProps {
  refInfo: DiscussionEntityRef;
  title?: string;
  currentUserId: string;
  currentUserName: string;
  getHeaders: () => Promise<Record<string, string>>;
  getAccessToken: () => Promise<string>;
  useApi?: boolean;
  useLive?: boolean;
}

const DiscussionPanel: React.FC<DiscussionPanelProps> = ({
  refInfo,
  title,
  currentUserId,
  currentUserName,
  getHeaders,
  getAccessToken,
  useApi = true,
  useLive = true,
}) => {
  const service = useMemo(() => {
    const repository = useApi
      ? createApiDiscussionRepository({
          baseUrl: config.apiBaseUrl,
          getHeaders,
        })
      : createLocalStorageDiscussionRepository();

    const realtime = useApi && useLive
      ? createSignalRDiscussionTransport({
          hubUrl: `${config.apiBaseUrl}/hubs/discussion`,
          getAccessToken,
        })
      : undefined;

    return createDiscussionService(repository, realtime);
  }, [getAccessToken, getHeaders, useApi, useLive]);

  const controller = useDiscussionController({
    service,
    ref: refInfo,
    currentUserId,
    currentUserName,
    liveEnabled: useApi && useLive,
  });

  return (
    <DiscussionCore
      title={title || 'Discussion'}
      messages={controller.messages}
      loading={controller.loading}
      error={controller.error}
      draft={controller.draft}
      sending={controller.sending}
      currentUserId={currentUserId}
      onDraftChange={controller.setDraft}
      onSend={controller.send}
    />
  );
};

export default DiscussionPanel;
